import { createLocalStorageSimpleWrapper } from '@/utils/cache'
import { getQueryStringValue } from '@/utils/querystring'
import { isUnitTest } from '@/utils/unit.js'
import {
  whitelistedEnvs,
  envNames as envNamesTable,
  toEnvFormat,
  toFriendlyNameFormat,
} from '@/config/envs-config'

const whitelistedFriendlyNames = Object.keys(whitelistedEnvs)
const envCacheManager = createLocalStorageSimpleWrapper('v3env_')

export const envNames = envNamesTable

const defaultCustomEnvs = {
  VITE_RELEASE_NAME:
    import.meta.env.RELEASE_NAME || import.meta.env.VITE_RELEASE_NAME,
  VITE_RELEASE_TIMESTAMP:
    import.meta.env.RELEASE_TIMESTAMP || import.meta.env.VITE_RELEASE_TIMESTAMP,
  //Test sentry locally
  //VITE_SENTRY_DSN:"https://0389040e65d44560b9fe03454743f432@sentry.geosab.eu/4"
}

let customEnvs = {}

/**
 * Custom envs are prioritized over system envs.
 * Custom envs are used by auth store to load client parameters.
 * @param {*} envs
 */
export function setCustomEnvs(envs) {
  for (let key in envs) {
    customEnvs[key] = envs[key]
  }
  for (let key in defaultCustomEnvs) {
    customEnvs[key] = defaultCustomEnvs[key]
  }
  console.debugVerbose(8, 'setCustomEnvs', {
    customEnvs,
  })
}

export function clearCustomEnvs() {
  customEnvs = Object.assign({}, defaultCustomEnvs)
}

/**
 *
 * @param {String} name
 * @param {String|Number} defaultValue
 * @param {Object} options
 * @param {Funtion} options.transform Transform function
 * @returns
 */
export function getEnvIntValue(name, defaultValue = '', options = {}) {
  return getEnvValue(name, defaultValue, {
    transform: (v) => parseInt(v) || defaultValue,
  })
}

/**
 * Wrapper to retrieve env values. Adapt to different frameworks if needed (i.g vite)
 * @param {*} providedName
 * @param {String|Number} defaultValue
 * @function getEnvValue
 * @param {Function} options.transform Transform value before return
 * @returns {String}
 */
export function getEnvValue(providedName, defaultValue = '', options = {}) {
  if (typeof providedName === 'function') {
    providedName = providedName(envNames)
  }

  let friendlyName = toFriendlyNameFormat(
    envNames[providedName] || providedName
  )
  let envName = toEnvFormat(friendlyName)

  const isUsingVite = typeof import.meta.env !== 'undefined'

  const isStagging =
    !envName.includes('GEORED_HOST') &&
    (!envService.isProduction() || envService.isTestEnv())

  let value = ''

  let warnMessage = ''
  if (isStagging && !whitelistedFriendlyNames.includes(friendlyName)) {
    warnMessage = `WARN: Add env to whitelist list (envs-config.js)`
  }

  // In test environment, check process.env first
  if (isUnitTest()) {
    value = process.env[envName] || ''
  }

  // If no value found in process.env or not in test environment, check import.meta.env
  if (!value) {
    value = import.meta.env[envName] || defaultValue
  }

  let wasOverrideViaClientParam = false
  if (
    customEnvs[friendlyName] ||
    customEnvs[envName] ||
    customEnvs[providedName]
  ) {
    value =
      customEnvs[friendlyName] ||
      customEnvs[envName] ||
      customEnvs[providedName]
    wasOverrideViaClientParam = true
  }

  //If stagging, override using localstorage
  let wasOverideViaLocalStorage = false
  if (
    (isStagging && envCacheManager.checkIfExists(friendlyName)) ||
    envCacheManager.checkIfExists(envName) ||
    envCacheManager.checkIfExists(providedName)
  ) {
    value =
      envCacheManager.get(friendlyName) ||
      envCacheManager.get(envName) ||
      getQueryStringValue(providedName)
    wasOverideViaLocalStorage = true
  }

  //If stagging, override using URL
  let wasOverrideViaQueryString = false
  if (
    (isStagging &&
      (getQueryStringValue(friendlyName).length >= 1 ||
        getQueryStringValue(envName).length >= 1)) ||
    getQueryStringValue(providedName).length >= 1
  ) {
    value =
      getQueryStringValue(friendlyName) ||
      getQueryStringValue(envName) ||
      getQueryStringValue(providedName)
    wasOverrideViaQueryString = true
  }

  console.debugVerboseScope(8, 'getEnvValue', {
    providedName,
    friendlyName,
    envName,
    isStagging,
    wasOverrideViaClientParam,
    wasOverideViaLocalStorage,
    wasOverrideViaQueryString,
    ...(warnMessage ? { warnMessage } : {}),
    value,
  })

  return options.transform ? options.transform(value) : value
}

/**
 * @todo Deprecate and use getEnvValue instead (It supports querystring on stagging envs). Remove
 * @param {*} name
 * @returns
 */
export function getDevOnlyQueryStringValue(name) {
  if (!envService.isProduction()) {
    return getEnvValue(name)
  } else {
    return ''
  }
}

export function isTestMode() {
  return getDevOnlyQueryStringValue('test') === '1'
}

export function isVerboseMode(str = '1') {
  return (
    (getQueryStringValue('verbose') || '').includes('1') ||
    (getQueryStringValue('verbose') || '').includes(str)
  )
}

export const getCustomEnvs = () => customEnvs

const envService = {
  getCustomEnvs,
  envCacheManager,
  isTestMode,
  getDevOnlyQueryStringValue,
  getEnvIntValue,
  getEnvValue,
  envs: import.meta.env,
  /**
   * @function isLocalhost
   * @alias $env.isLocalhost
   * @public
   * @returns {boolean}
   */
  isLocalhost: function () {
    return (
      window.location.origin.indexOf('localhost') !== -1 ||
      window.location.origin.includes('80')
    )
  },
  /**
   * @function isDev
   * @alias $env.isDev
   * @public
   * @returns {boolean}
   */
  isDev: function () {
    return (
      window.location.origin.indexOf('dev-') !== -1 ||
      (this.getEnvValue('VITE_API_GEORED_HOST') || '').indexOf('dev-') !== -1
    )
  },
  isRecette() {
    return (
      window.location.origin.indexOf('recette-') !== -1 ||
      (this.getEnvValue('VITE_API_GEORED_HOST') || '').indexOf('recette-') !==
        -1
    )
  },
  isIsoprod() {
    return (
      window.location.origin.indexOf('isoprod-') !== -1 ||
      (this.getEnvValue('VITE_API_GEORED_HOST') || '').indexOf('isoprod-') !==
        -1
    )
  },
  isBeta() {
    return (
      window.location.origin.indexOf('beta-') !== -1 ||
      (this.getEnvValue('VITE_API_GEORED_HOST') || '').indexOf('beta-') !== -1
    )
  },
  /**
   * @returns True if domain name includes "-test" (beta-test/preprod-test)
   */
  isTestEnv() {
    return window.location.origin.indexOf('-test') !== -1
  },
  isPreprod() {
    return (
      window.location.origin.indexOf('preprod-') !== -1 ||
      (this.getEnvValue('VITE_API_GEORED_HOST') || '').indexOf('preprod-') !==
        -1
    )
  },
  getEnvName() {
    if (this.isDev()) return 'dev'
    if (this.isRecette()) return 'recette'
    if (this.isIsoprod()) return 'isoprod'
    if (this.isBeta()) return 'beta'
    if (this.isPreprod()) return 'preprod'
    return 'production'
  },
  /**
   * This flag will normally enable test behaviours and test modes across the app (fake data, rights bypass)
   * @returns
   */
  isStagging() {
    return (
      this.isLocalhost() || this.isDev() || this.isRecette() || this.isIsoprod()
    )
  },
  /**
   *
   * This flag will normally disable any test behaviour across the app.
   *
   * @function isProduction
   * @alias $env.isProduction
   * @public
   * @returns {boolean}
   */
  isProduction() {
    //Test case
    if (isUnitTest() && import.meta.env.NODE_ENV === 'production') {
      return true
    }

    return !isUnitTest() && !this.isStagging()
  },
  isUnitTest,
  nodeEnv: import.meta.env.NODE_ENV,
}
export default envService
